import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Plus, GraduationCap, Users, ClipboardText, ArrowRight, Sparkle, UsersThree, X, Crown, Lightbulb, CaretDown, MagicWand,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const GUIDE_DISMISS_KEY = "ekkol_teacher_guide_dismissed";

export default function TeacherClasses() {
  const { user } = useAuth();
  const canTeach = !!(
    user?.can_teach_classes ||
    user?.is_admin ||
    user?.plan === "edu" ||
    user?.plan === "team"
  );
  const [taught, setTaught] = useState([]);
  const [enrolled, setEnrolled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [guideOpen, setGuideOpen] = useState(() => {
    try { return localStorage.getItem(GUIDE_DISMISS_KEY) !== "1"; } catch { return true; }
  });
  // Class-from-topic wizard state
  const [showWizard, setShowWizard] = useState(false);
  const [wizClassName, setWizClassName] = useState("");
  const [wizTopic, setWizTopic] = useState("");
  const [wizCount, setWizCount] = useState(15);
  const [wizStep, setWizStep] = useState(0); // 0 idle, 1 deck, 2 class, 3 assign
  const [wizRunning, setWizRunning] = useState(false);
  const navigate = useNavigate();

  const dismissGuide = () => {
    try { localStorage.setItem(GUIDE_DISMISS_KEY, "1"); } catch {}
    setGuideOpen(false);
  };

  const runWizard = async () => {
    const topic = wizTopic.trim();
    const className = wizClassName.trim();
    if (!topic) { toast.error("Enter a topic"); return; }
    if (!className) { toast.error("Enter a class name"); return; }
    setWizRunning(true);
    setWizStep(1);
    try {
      // 1. Generate Q&A cards from topic
      const { data: qa } = await api.post("/ai/generate-qa", {
        topic,
        count: Math.max(5, Math.min(30, Number(wizCount) || 15)),
      });
      if (!qa.items?.length) {
        toast.error("AI couldn't generate cards — try a more specific topic");
        setWizRunning(false); setWizStep(0);
        return;
      }
      // 2. Create the deck (Q&A / flashcard style)
      const { data: deck } = await api.post("/decks", {
        title: topic,
        description: `Generated from topic: ${topic}`,
        mode: "qa",
        qa_style: "flashcard",
        chunk_size: "medium",
        match_mode: "concept",
        match_threshold: 75,
        auto_advance: true,
      });
      const cards = qa.items
        .filter((q) => q.prompt?.trim() && q.answer?.trim())
        .map((q) => ({
          prompt: q.prompt.trim(),
          content: q.answer.trim(),
          alt_answers: [],
        }));
      if (cards.length) {
        await api.post(`/decks/${deck.deck_id}/cards`, cards);
      }
      setWizStep(2);
      // 3. Create the class
      const { data: cls } = await api.post("/classes", {
        name: className,
        description: `Auto-created with "${topic}" starter deck`,
      });
      setWizStep(3);
      // 4. Assign the deck to the class
      await api.post(`/classes/${cls.class_id}/assignments`, {
        deck_id: deck.deck_id,
        title: topic,
      });
      toast.success(`Class "${className}" created with ${cards.length} cards · join code ${cls.join_code}`);
      setShowWizard(false);
      setWizClassName(""); setWizTopic(""); setWizStep(0);
      navigate(`/classes/${cls.class_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wizard failed — try again or build it step-by-step");
      setWizStep(0);
    } finally {
      setWizRunning(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/classes/mine");
      setTaught(data.taught || []);
      setEnrolled(data.enrolled || []);
    } catch {
      toast.error("Could not load your classes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createClass = async () => {
    if (!name.trim()) { toast.error("Give the class a name"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/classes", { name: name.trim(), description: description.trim() || null });
      toast.success(`Class created · join code ${data.join_code}`);
      setShowCreate(false);
      setName(""); setDescription("");
      navigate(`/classes/${data.class_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create class");
    } finally { setCreating(false); }
  };

  const joinByCode = async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length < 4) { toast.error("Enter the 6-letter code"); return; }
    setJoining(true);
    try {
      const { data } = await api.post("/classes/join", { code });
      toast.success(data.already_member ? "You're already in this class" : "Joined!");
      setShowJoin(false);
      setJoinCode("");
      navigate(`/classes/${data.class_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join");
    } finally { setJoining(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[color:var(--paper)]"><Navbar/>
      <div className="max-w-5xl mx-auto py-20 px-6"><div className="wave"><span/><span/><span/><span/><span/></div></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-12 fade-in">
        <div className="flex items-start justify-between flex-wrap gap-6 border-b rule pb-8 mb-10">
          <div>
            <div className="eyebrow mb-3">Classes</div>
            <h1 className="font-serif text-4xl sm:text-5xl leading-none tracking-tight">Your classes</h1>
            <p className="mt-4 text-[color:var(--ink-muted)] max-w-2xl">
              Teach with Ekkol: create a class, share the 6-letter join code with your students, assign decks,
              and watch per-student mastery in real time. Join a teacher&apos;s class with a code they share with you.
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => setShowJoin(true)} className="btn-ghost" data-testid="btn-join-class">
              <Users size={16}/> Join with code
            </button>
            {canTeach && (
              <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="btn-create-class">
                <Plus size={16} weight="bold"/> Create class
              </button>
            )}
          </div>
        </div>

        {/* Teacher Quick Start guide — collapsible + dismissible */}
        {canTeach && (
          <section className="mb-10" data-testid="teacher-guide">
            <div className="border rule bg-white">
              <button
                onClick={() => setGuideOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-[color:var(--surface-alt)] transition-colors"
                data-testid="btn-toggle-guide"
                aria-expanded={guideOpen}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[color:var(--oxford)]/10 flex items-center justify-center">
                    <Lightbulb size={18} weight="duotone" className="text-[color:var(--oxford)]"/>
                  </div>
                  <div>
                    <div className="eyebrow">Quick start</div>
                    <h3 className="font-serif text-lg leading-tight">Teacher guide · {guideOpen ? "click to collapse" : "click to expand"}</h3>
                  </div>
                </div>
                <CaretDown size={16} className={`transition-transform ${guideOpen ? "rotate-180" : ""}`}/>
              </button>

              {guideOpen && (
                <div className="border-t rule px-6 py-6 fade-in">
                  <ol className="space-y-5">
                    <li className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[color:var(--ink)] text-white font-mono flex items-center justify-center text-sm">1</div>
                      <div className="flex-1">
                        <div className="font-serif text-lg">Make a deck first.</div>
                        <p className="text-sm text-[color:var(--ink-muted)] mt-1">
                          Every assignment is backed by a deck. Go to <Link to="/decks/new" className="link-underline text-[color:var(--ink)] font-medium">New deck</Link> — paste text, upload a PDF/Word doc, or let AI generate flashcards from a topic (e.g. "ITIL 4 Foundation" · 20 cards).
                        </p>
                      </div>
                    </li>
                    <li className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[color:var(--ink)] text-white font-mono flex items-center justify-center text-sm">2</div>
                      <div className="flex-1">
                        <div className="font-serif text-lg">Create a class and share the code.</div>
                        <p className="text-sm text-[color:var(--ink-muted)] mt-1">
                          Click <em>Create class</em> below — you&apos;ll get a 6-letter join code (like <span className="font-mono">BVX7KP</span>). Share it in class, by email, or paste your roster on the Roster tab for instant enrollment. Students don&apos;t need Edu — the Free plan is fine.
                        </p>
                      </div>
                    </li>
                    <li className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[color:var(--ink)] text-white font-mono flex items-center justify-center text-sm">3</div>
                      <div className="flex-1">
                        <div className="font-serif text-lg">Assign a deck with a due date.</div>
                        <p className="text-sm text-[color:var(--ink-muted)] mt-1">
                          Open the class → Assignments tab → <em>New assignment</em>. Pick one of your decks, set a due date, and it&apos;s instantly in every student&apos;s library. Edit the deck later and the changes flow to every student automatically.
                        </p>
                      </div>
                    </li>
                    <li className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[color:var(--ink)] text-white font-mono flex items-center justify-center text-sm">4</div>
                      <div className="flex-1">
                        <div className="font-serif text-lg">Watch mastery roll in.</div>
                        <p className="text-sm text-[color:var(--ink-muted)] mt-1">
                          Progress tab shows every student × every assignment. Cells turn green at ≥80% mastery. <strong>Export CSV</strong> for your gradebook (Google Sheets, Excel, PowerSchool, Infinite Campus).
                        </p>
                      </div>
                    </li>
                  </ol>

                  <div className="mt-6 pt-5 border-t rule grid sm:grid-cols-2 gap-4">
                    <div>
                      <div className="eyebrow mb-2">Tools your students will love</div>
                      <ul className="text-sm space-y-1.5 text-[color:var(--ink-muted)]">
                        <li>🎧 Hands-free study mode (on a walk / as a passenger)</li>
                        <li>💡 Progressive AI hints (3 levels)</li>
                        <li>🗣️ Concept-match grading — paraphrase freely</li>
                        <li>🔁 Retry-missed at the end of every session</li>
                      </ul>
                    </div>
                    <div>
                      <div className="eyebrow mb-2">Connect to your tools</div>
                      <ul className="text-sm space-y-1.5 text-[color:var(--ink-muted)]">
                        <li>✅ <strong>Google Classroom</strong> · one-click share (on any public deck)</li>
                        <li>✅ <strong>CSV gradebook export</strong> · Progress tab</li>
                        <li>🛠️ <strong>SCORM, LTI 1.3, Canvas, Moodle</strong> — on the roadmap. Need it now? <a className="link-underline font-medium text-[color:var(--ink)]" href="mailto:sales@ekkol.app?subject=LMS%20integration%20early%20access">Email sales</a> for free early access.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center gap-3 flex-wrap">
                    <button
                      onClick={() => setShowWizard(true)}
                      className="btn-primary py-2 px-4 text-sm"
                      data-testid="guide-quick-start-wizard"
                    >
                      <MagicWand size={14} weight="fill"/> Generate a class from a topic
                    </button>
                    <Link to="/decks/new" className="btn-ghost py-2 px-4 text-sm" data-testid="guide-new-deck">
                      <Plus size={14} weight="bold"/> Or build a deck manually
                    </Link>
                    <Link to="/help#teachers-classes" className="btn-ghost py-2 px-4 text-sm">
                      Read full teacher FAQ
                    </Link>
                    <button onClick={dismissGuide} className="text-xs link-underline ml-auto text-[color:var(--ink-muted)]" data-testid="btn-dismiss-guide">
                      Don&apos;t show this again
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Classes I teach */}
        {canTeach && (
        <section className="mb-12">
          <div className="flex items-center gap-2 eyebrow mb-4">
            <GraduationCap size={14}/> Teaching
          </div>
          {taught.length === 0 ? (
            <div className="border rule bg-white p-10 text-center">
              <UsersThree size={36} weight="duotone" className="mx-auto text-[color:var(--ink-muted)]"/>
              <p className="mt-4 font-serif text-xl">No classes yet.</p>
              <p className="mt-2 text-sm text-[color:var(--ink-muted)] max-w-md mx-auto">
                Teachers create a class here, then assign decks as homework. Each class gets a shareable join code.
              </p>
              <button onClick={() => setShowCreate(true)} className="btn-primary mt-6" data-testid="btn-create-first-class">
                <Sparkle size={14} weight="fill"/> Create your first class
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="taught-list">
              {taught.map((c) => (
                <Link
                  to={`/classes/${c.class_id}`}
                  key={c.class_id}
                  className="border rule bg-white p-6 hover:border-[color:var(--ink)] transition-colors group"
                  data-testid={`taught-class-${c.class_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="eyebrow mb-2">Class</div>
                      <h3 className="font-serif text-2xl leading-snug">{c.name}</h3>
                      {c.description && <p className="text-sm text-[color:var(--ink-muted)] mt-2">{c.description}</p>}
                    </div>
                    <span className="font-mono text-xs uppercase tracking-[0.2em] bg-[color:var(--ink)] text-white px-2 py-1">
                      {c.join_code}
                    </span>
                  </div>
                  <div className="mt-5 flex items-center gap-5 text-xs text-[color:var(--ink-muted)] font-mono">
                    <span className="inline-flex items-center gap-1"><Users size={12}/> {c.member_count ?? 0} members</span>
                    <span className="inline-flex items-center gap-1"><ClipboardText size={12}/> {c.assignment_count ?? 0} assignments</span>
                  </div>
                  <div className="mt-4 text-sm link-underline inline-flex items-center gap-1 text-[color:var(--ink)] group-hover:text-[color:var(--vermilion)] transition-colors">
                    Open class <ArrowRight size={14}/>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
        )}

        {/* Upgrade CTA for users without Edu plan and not enrolled anywhere */}
        {!canTeach && enrolled.length === 0 && (
          <section
            className="border rule bg-white p-10 text-center mb-12"
            data-testid="classes-upgrade-cta"
          >
            <Crown size={36} weight="duotone" className="mx-auto text-[color:var(--oxford)]"/>
            <p className="mt-4 font-serif text-2xl">Classes are part of the Edu plan.</p>
            <p className="mt-3 text-sm text-[color:var(--ink-muted)] max-w-lg mx-auto">
              The Edu plan gives teachers a dashboard to create classes, invite students, assign decks,
              and track per-student progress. Students don&apos;t need Edu to join — they just need a
              teacher&apos;s 6-letter code.
            </p>
            <div className="mt-6 flex justify-center gap-3 flex-wrap">
              <Link to="/pricing" className="btn-primary" data-testid="btn-see-edu-pricing">
                <Crown size={14} weight="fill"/> See Edu pricing
              </Link>
              <button onClick={() => setShowJoin(true)} className="btn-ghost" data-testid="btn-join-with-code-cta">
                <Users size={14}/> I have a join code
              </button>
            </div>
          </section>
        )}

        {/* Classes I'm enrolled in */}
        {enrolled.length > 0 && (
          <section>
            <div className="flex items-center gap-2 eyebrow mb-4">
              <Users size={14}/> Enrolled as a student
            </div>
            <div className="grid md:grid-cols-2 gap-4" data-testid="enrolled-list">
              {enrolled.map((c) => (
                <Link
                  to={`/classes/${c.class_id}`}
                  key={c.class_id}
                  className="border rule bg-white p-6 hover:border-[color:var(--ink)] transition-colors"
                  data-testid={`enrolled-class-${c.class_id}`}
                >
                  <div className="eyebrow mb-2">Class</div>
                  <h3 className="font-serif text-xl">{c.name}</h3>
                  {c.description && <p className="text-sm text-[color:var(--ink-muted)] mt-2">{c.description}</p>}
                  <div className="mt-4 text-xs text-[color:var(--ink-muted)] font-mono">
                    {c.assignment_count ?? 0} assignment{(c.assignment_count ?? 0) === 1 ? "" : "s"}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Create modal */}
        {showCreate && (
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 fade-in"
            onClick={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}
            data-testid="create-class-modal"
          >
            <div className="bg-white border rule w-full max-w-md">
              <div className="flex items-center justify-between border-b rule px-6 py-4">
                <div>
                  <div className="eyebrow">New class</div>
                  <h2 className="font-serif text-2xl leading-none mt-1">Create a class</h2>
                </div>
                <button onClick={() => setShowCreate(false)} aria-label="Close" className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"><X size={20}/></button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="eyebrow block mb-2">Class name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && createClass()}
                    placeholder="e.g. Biology 201 — Fall 2026"
                    autoFocus
                    className="w-full bg-white border rule px-4 py-3 text-lg focus:outline-none focus:border-[color:var(--ink)]"
                    data-testid="input-class-name"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Description (optional)</label>
                  <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g. Period 3, room 214"
                    className="w-full bg-white border rule px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                    data-testid="input-class-description"
                  />
                </div>
                <p className="text-xs text-[color:var(--ink-muted)]">
                  A 6-letter join code will be generated. Share it with your students so they can join.
                </p>
              </div>
              <div className="flex items-center justify-end gap-3 border-t rule px-6 py-4">
                <button onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
                <button onClick={createClass} disabled={creating} className="btn-primary" data-testid="btn-create-confirm">
                  <Sparkle size={14} weight="fill"/> {creating ? "Creating…" : "Create class"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Wizard modal: Generate a class from a topic */}
        {showWizard && (
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 fade-in"
            onClick={(e) => { if (e.target === e.currentTarget && !wizRunning) setShowWizard(false); }}
            data-testid="wizard-modal"
          >
            <div className="bg-white border rule w-full max-w-lg">
              <div className="flex items-center justify-between border-b rule px-6 py-4">
                <div className="flex items-center gap-2">
                  <MagicWand size={18} weight="duotone" className="text-[color:var(--oxford)]"/>
                  <div>
                    <div className="eyebrow">One-click setup</div>
                    <h2 className="font-serif text-2xl leading-none mt-1">Generate a class</h2>
                  </div>
                </div>
                {!wizRunning && (
                  <button onClick={() => setShowWizard(false)} aria-label="Close" className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]">
                    <X size={20}/>
                  </button>
                )}
              </div>
              <div className="px-6 py-5 space-y-4">
                <p className="text-sm text-[color:var(--ink-muted)]">
                  Tell us what you&apos;re teaching. We&apos;ll generate a flashcard deck, create the class, and assign
                  the deck — all in one click. You can edit cards after if anything looks off.
                </p>
                <div>
                  <label className="eyebrow block mb-2">Class name</label>
                  <input
                    value={wizClassName}
                    onChange={(e) => setWizClassName(e.target.value)}
                    disabled={wizRunning}
                    placeholder="e.g. Biology 201 — Fall 2026"
                    autoFocus
                    className="w-full bg-white border rule px-4 py-3 text-base focus:outline-none focus:border-[color:var(--ink)] disabled:opacity-50"
                    data-testid="input-wizard-class-name"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Topic to study</label>
                  <input
                    value={wizTopic}
                    onChange={(e) => setWizTopic(e.target.value)}
                    disabled={wizRunning}
                    placeholder="e.g. Mitosis and meiosis · or · ITIL 4 Foundation · or · US Constitution Amendments"
                    className="w-full bg-white border rule px-4 py-3 text-base focus:outline-none focus:border-[color:var(--ink)] disabled:opacity-50"
                    data-testid="input-wizard-topic"
                  />
                  <p className="text-xs text-[color:var(--ink-faint)] mt-2">
                    The more specific, the better. "US Civil War · key battles" beats just "history".
                  </p>
                </div>
                <div>
                  <label className="eyebrow block mb-2">How many cards? <span className="font-mono text-[color:var(--ink)]">{wizCount}</span></label>
                  <input
                    type="range"
                    min={5}
                    max={30}
                    value={wizCount}
                    onChange={(e) => setWizCount(Number(e.target.value))}
                    disabled={wizRunning}
                    className="w-full"
                    data-testid="input-wizard-count"
                  />
                  <div className="flex justify-between text-xs text-[color:var(--ink-faint)] font-mono mt-1">
                    <span>5</span><span>quiz-size</span><span>30</span>
                  </div>
                </div>

                {wizRunning && (
                  <div className="border rule bg-[color:var(--surface-alt)] p-4 space-y-2 fade-in" data-testid="wizard-progress">
                    <div className="wave mb-2"><span/><span/><span/><span/><span/></div>
                    <ol className="text-sm space-y-1">
                      <li className={wizStep >= 1 ? "text-[color:var(--ink)]" : "text-[color:var(--ink-faint)]"}>
                        {wizStep > 1 ? "✓" : wizStep === 1 ? "·" : "○"} Generating cards with AI…
                      </li>
                      <li className={wizStep >= 2 ? "text-[color:var(--ink)]" : "text-[color:var(--ink-faint)]"}>
                        {wizStep > 2 ? "✓" : wizStep === 2 ? "·" : "○"} Creating the class…
                      </li>
                      <li className={wizStep >= 3 ? "text-[color:var(--ink)]" : "text-[color:var(--ink-faint)]"}>
                        {wizStep >= 3 ? "·" : "○"} Assigning the deck…
                      </li>
                    </ol>
                    <div className="progress-track mt-2"/>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-3 border-t rule px-6 py-4">
                <button
                  onClick={() => setShowWizard(false)}
                  disabled={wizRunning}
                  className="btn-ghost"
                >
                  Cancel
                </button>
                <button
                  onClick={runWizard}
                  disabled={wizRunning || !wizTopic.trim() || !wizClassName.trim()}
                  className="btn-primary"
                  data-testid="btn-run-wizard"
                >
                  <MagicWand size={14} weight="fill"/> {wizRunning ? "Working…" : "Generate"}
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Join modal */}
        {showJoin && (
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 fade-in"
            onClick={(e) => { if (e.target === e.currentTarget) setShowJoin(false); }}
            data-testid="join-class-modal"
          >
            <div className="bg-white border rule w-full max-w-md">
              <div className="flex items-center justify-between border-b rule px-6 py-4">
                <div>
                  <div className="eyebrow">Join a class</div>
                  <h2 className="font-serif text-2xl leading-none mt-1">Enter your code</h2>
                </div>
                <button onClick={() => setShowJoin(false)} aria-label="Close" className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"><X size={20}/></button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <input
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && joinByCode()}
                  maxLength={6}
                  placeholder="e.g. BVX7KP"
                  autoFocus
                  className="w-full bg-white border rule px-4 py-4 text-center text-3xl font-mono uppercase tracking-[0.4em] focus:outline-none focus:border-[color:var(--ink)]"
                  data-testid="input-join-code"
                />
                <p className="text-xs text-[color:var(--ink-muted)]">
                  Your teacher shares this code in class or by email.
                </p>
              </div>
              <div className="flex items-center justify-end gap-3 border-t rule px-6 py-4">
                <button onClick={() => setShowJoin(false)} className="btn-ghost">Cancel</button>
                <button onClick={joinByCode} disabled={joining} className="btn-primary" data-testid="btn-join-confirm">
                  <Users size={14}/> {joining ? "Joining…" : "Join class"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
